import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid,
  MenuItem, CircularProgress, Tooltip, Alert, InputAdornment, Stack, Divider,
  Checkbox, ListItemText, ListItemIcon,
} from '@mui/material';
import {
  Add, Edit, Delete, Close, Search, Category as CategoryIcon,
  AutoAwesome, Refresh, Tune, VolunteerActivism, CheckCircleOutline, DonutSmall,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  incomeTypesAllURL, incomeTypesAddURL, incomeTypesUpdateURL, incomeTypesDeleteURL,
  incomeCategoriesURL, incomeCategoriesAddURL, incomeCategoriesUpdateURL, incomeCategoriesDeleteURL,
} from '../BaseURL';
import CategoryManagerDialog from './CategoryManagerDialog';

function headers() { return { Authorization: `Bearer ${localStorage.getItem('church_token')}` }; }
const swalTop = { customClass: { container: 'swal-on-top' } };
const EMPTY = { name: '', category: '', narration: '', status: 'active' };

export default function IncomeTypes() {
  const [types, setTypes]         = useState([]);
  const [cats, setCats]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState('');
  const [open, setOpen]           = useState(false);
  const [catsOpen, setCatsOpen]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState('');
  const [search, setSearch]       = useState('');
  const [catFilter, setCatFilter] = useState([]);

  useEffect(() => {
    if (!document.getElementById('swal-top')) {
      const s = document.createElement('style');
      s.id = 'swal-top'; s.innerHTML = '.swal-on-top{z-index:99999!important}';
      document.head.appendChild(s);
    }
  }, []);

  const colorFor = (cat) => cats.find(c => c.name === cat)?.color || '#64748B';
  const prefixFor = (cat) => cats.find(c => c.name === cat)?.code_prefix || 'INC';

  const loadCats = async () => {
    try {
      const r = await axios.get(incomeCategoriesURL, { headers: headers() });
      if (r.data.success) setCats(r.data.data);
    } catch { /* no-op */ }
  };

  const loadTypes = () => {
    setLoading(true); setLoadError('');
    axios.get(incomeTypesAllURL, { headers: headers() })
      .then(r => { if (r.data.success) setTypes(r.data.data); else setLoadError(r.data.message); })
      .catch(err => setLoadError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadCats(); loadTypes(); }, []);

  const filtered = useMemo(() => types.filter(t => {
    const q = search.toLowerCase();
    const matchesSearch = !q
      || t.name.toLowerCase().includes(q)
      || (t.category || '').toLowerCase().includes(q)
      || (t.account_code || '').toLowerCase().includes(q);
    const matchesCat = catFilter.length === 0 || catFilter.includes(t.category);
    return matchesSearch && matchesCat;
  }), [types, search, catFilter]);

  const catCounts = useMemo(() => {
    const m = {};
    for (const c of cats) m[c.name] = 0;
    for (const t of types) m[t.category] = (m[t.category] || 0) + 1;
    return m;
  }, [types, cats]);

  const activeCount = useMemo(() => types.filter(t => t.status === 'active').length, [types]);

  const previewCode = (cat) => {
    if (!cat) return '';
    const prefix = prefixFor(cat);
    const nums = types
      .filter(t => (t.account_code || '').startsWith(prefix + '-'))
      .map(t => parseInt((t.account_code || '').split('-')[1], 10))
      .filter(n => !isNaN(n));
    const next = (nums.length ? Math.max(...nums) : 0) + 1;
    return `${prefix}-${String(next).padStart(3, '0')}`;
  };

  const openAdd = () => { setEditing(null); setSaveError(''); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t); setSaveError('');
    setForm({ name: t.name, category: t.category || '', narration: t.narration || '', status: t.status });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setSaveError('Income type name is required.'); return; }
    setSaving(true); setSaveError('');
    try {
      if (editing) {
        await axios.post(incomeTypesUpdateURL, { id: editing.id, ...form }, { headers: headers() });
      } else {
        await axios.post(incomeTypesAddURL, form, { headers: headers() });
      }
      setOpen(false);
      loadTypes();
      Swal.fire({ ...swalTop, icon: 'success', title: editing ? 'Updated' : 'Income type added', timer: 1400, showConfirmButton: false });
    } catch (err) {
      setSaveError(err.response?.data?.message || 'Could not save.');
    } finally { setSaving(false); }
  };

  const handleDelete = (t) => {
    Swal.fire({
      ...swalTop, title: `Delete "${t.name}"?`,
      text: 'This income type will be removed. Existing entries will keep the name.', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#B91C1C', confirmButtonText: 'Delete',
    }).then(async r => {
      if (r.isConfirmed) {
        try {
          await axios.post(incomeTypesDeleteURL, { id: t.id }, { headers: headers() });
          loadTypes();
        } catch (err) {
          Swal.fire({ ...swalTop, icon: 'error', title: 'Error', text: err.response?.data?.message || 'Could not delete.' });
        }
      }
    });
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="caption" color="secondary.main" fontWeight={700} sx={{ letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Configuration
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.02em', mt: 0.5 }}>
            Income Types
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Classify all incoming funds. Account codes are generated automatically from the category.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<Tune />} onClick={() => setCatsOpen(true)}>Manage Categories</Button>
          <Button variant="outlined" startIcon={<Refresh />} onClick={() => { loadCats(); loadTypes(); }}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={openAdd} size="large" sx={{ px: 3 }}>
            Add Income Type
          </Button>
        </Stack>
      </Box>

      {loadError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setLoadError('')}>{loadError}</Alert>}

      {/* Stat strip */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total income types', value: types.length,  icon: <VolunteerActivism />,  color: 'primary.main' },
          { label: 'Active',             value: activeCount,   icon: <CheckCircleOutline />, color: 'success.main' },
          { label: 'Categories',         value: cats.length,   icon: <DonutSmall />,         color: 'secondary.main' },
        ].map(s => (
          <Grid item xs={12} sm={4} key={s.label}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  width: 42, height: 42, borderRadius: 1.5,
                  bgcolor: s.color, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {s.icon}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, fontSize: 10 }}>
                    {s.label}
                  </Typography>
                  <Typography variant="h5" fontWeight={800}>{s.value}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by name, category, or code…"
            value={search} onChange={e => setSearch(e.target.value)} size="small"
            sx={{ flex: 1, maxWidth: 380 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select size="small" label="Filter by category"
            value={catFilter}
            onChange={e => setCatFilter(
              typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
            )}
            InputLabelProps={{ shrink: true }}
            InputProps={{ notched: true }}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              renderValue: (selected) =>
                selected.length === 0
                  ? <Typography variant="body2" color="text.secondary">All categories</Typography>
                  : <Typography variant="body2" fontWeight={600}>{selected.length} selected</Typography>,
            }}
            sx={{ minWidth: 240 }}
          >
            {cats.length === 0 && (
              <MenuItem disabled>No categories defined</MenuItem>
            )}
            {cats.map(c => (
              <MenuItem key={c.id} value={c.name} dense>
                <Checkbox size="small" checked={catFilter.includes(c.name)} />
                <ListItemIcon sx={{ minWidth: 20 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c.color || '#64748B' }} />
                </ListItemIcon>
                <ListItemText
                  primary={c.name}
                  secondary={`${catCounts[c.name] || 0} type(s)`}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </MenuItem>
            ))}
          </TextField>
          {catFilter.length > 0 && (
            <Button size="small" onClick={() => setCatFilter([])}>Clear</Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            Showing {filtered.length} of {types.length}
          </Typography>
        </Box>
        <Divider />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={50}>#</TableCell>
                <TableCell>Income Type</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Default Narration</TableCell>
                <TableCell>Account Code</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" width={110}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}><CircularProgress size={26} /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    <CategoryIcon sx={{ fontSize: 40, color: '#CBD5E1', mb: 1 }} />
                    <Typography variant="body2">
                      {types.length === 0 ? 'No income types yet. Click "Add Income Type" to create your first.' : 'No results match your filter.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filtered.map((t, i) => (
                <TableRow key={t.id} hover>
                  <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                  <TableCell><Typography variant="body2" fontWeight={600}>{t.name}</Typography></TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colorFor(t.category), flexShrink: 0 }} />
                      <Typography variant="body2" fontWeight={500}>{t.category || '—'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 12, maxWidth: 260 }}>
                    {t.narration || <span style={{ color: '#CBD5E1' }}>—</span>}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t.account_code || '—'} size="small" variant="outlined"
                      color="primary"
                      sx={{ fontFamily: 'monospace', fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small" label={t.status}
                      color={t.status === 'active' ? 'success' : 'default'}
                      variant={t.status === 'active' ? 'filled' : 'outlined'}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(t)}><Edit fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(t)}><Delete fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Add / Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 2,
          background: 'linear-gradient(90deg, #0B2545, #13315C)', color: '#fff', py: 2.5,
        }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5, bgcolor: 'secondary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {editing ? <Edit /> : <Add />}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {editing ? 'Edit Income Type' : 'Add Income Type'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {editing ? 'Update the name, category or narration.' : 'The account code is generated for you.'}
            </Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {saveError && (
            <Box sx={{ px: 3, pt: 2 }}>
              <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setSaveError('')}>{saveError}</Alert>
            </Box>
          )}

          {cats.length === 0 && (
            <Box sx={{ px: 3, pt: 2 }}>
              <Alert severity="warning" sx={{ borderRadius: 2 }}
                action={<Button size="small" color="inherit" onClick={() => setCatsOpen(true)}>Create Categories</Button>}>
                No income categories exist yet. Create one before adding an income type.
              </Alert>
            </Box>
          )}

          <Box sx={{ p: 3 }}>
            <Typography variant="overline" fontWeight={700} color="primary.main" sx={{ letterSpacing: '0.14em' }}>
              Income Details
            </Typography>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label="Income Type Name" required
                  placeholder="e.g. SUNDAY OFFERING"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value.toUpperCase() })}
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                />
              </Grid>
              <Grid item xs={12} sm={7}>
                <TextField
                  select fullWidth size="small" label="Category"
                  value={form.category}
                  onChange={e => setForm({ ...form, category: e.target.value })}
                >
                  {cats.map(c => (
                    <MenuItem key={c.id} value={c.name}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c.color || '#64748B' }} />
                        {c.name}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth size="small" label="Account Code"
                  value={editing
                    ? (form.category && form.category.toUpperCase() !== (editing.category || '').toUpperCase()
                        ? previewCode(form.category)
                        : editing.account_code || '')
                    : previewCode(form.category)}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Tooltip title="Auto-generated from the selected category">
                          <AutoAwesome sx={{ fontSize: 16, color: 'secondary.main' }} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                    sx: { fontFamily: 'monospace', fontWeight: 700 },
                  }}
                  helperText="Generated automatically"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label="Default Narration (optional)"
                  value={form.narration}
                  onChange={e => setForm({ ...form, narration: e.target.value })}
                  multiline minRows={2}
                  placeholder="Short default description used when recording income of this type."
                />
              </Grid>
              {editing && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    select fullWidth size="small" label="Status"
                    value={form.status}
                    onChange={e => setForm({ ...form, status: e.target.value })}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </TextField>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#FAFBFC' }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ px: 3 }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : editing ? 'Save Changes' : 'Add Income Type'}
          </Button>
        </DialogActions>
      </Dialog>

      <CategoryManagerDialog
        open={catsOpen}
        onClose={() => setCatsOpen(false)}
        title="Manage Income Categories"
        listURL={incomeCategoriesURL}
        addURL={incomeCategoriesAddURL}
        updateURL={incomeCategoriesUpdateURL}
        deleteURL={incomeCategoriesDeleteURL}
        onChanged={loadCats}
      />
    </Box>
  );
}
